'use client';

import React, { useEffect, useState } from 'react';
import {
  Drawer,
  Box,
  Typography,
  TextField,
  Stack,
  Button,
  IconButton,
  MenuItem,
} from '@mui/material';
import { Add, Delete } from '@mui/icons-material';
import { v4 as uuidv4 } from 'uuid';
import axiosClient from '../../api/axiosClient';
import { ADVANTAGE_TYPE } from './CategoriesView';

interface AdvantageForm {
  id?: number;
  type: string;
  yearlyLimit: string | number;
  companyId?: number;
  version?: number;
}

interface CategoryData {
  id?: number;
  name: string;
  advantages: AdvantageForm[];
}

interface AddCategoryDrawerProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  editingCategory?: CategoryData | null;
}

export default function AddCategoryDrawer({
  open,
  onClose,
  onSuccess,
  editingCategory,
}: AddCategoryDrawerProps) {
  const [name, setName] = useState('');
const [advantages, setAdvantages] = useState<AdvantageForm[]>([
  { type: 'RESTAURANT', yearlyLimit: '' },
]);

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (editingCategory) {
      setName(editingCategory.name);
      setAdvantages(editingCategory.advantages || []);
    } else {
      setName('');
      setAdvantages([{ type: 'RESTAURANT', yearlyLimit: 0 }]);
    }
  }, [editingCategory]);

  const handleAddAdvantage = () => {
    setAdvantages([...advantages, { type: 'RESTAURANT', yearlyLimit: 0 }]);
  };

  const handleRemoveAdvantage = (index: number) => {
    setAdvantages(advantages.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    if (!name.trim()) return;
const payload = {
  name,
  advantages: advantages.map((adv) => ({
    ...adv,
    yearlyLimit: adv.yearlyLimit === '' ? 0 : Number(adv.yearlyLimit),
    companyId: 1,
    version: adv.version || 1,
  })),
};


    try {
      setLoading(true);

      if (editingCategory?.id) {
       
        await axiosClient.put(`/categories/${editingCategory.id}`, payload);
      } else {
    
        await axiosClient.post('/categories', payload, {
          headers: {
            'Idempotency-Key': uuidv4(), 
          },
        });
      }

      onSuccess();
      onClose();
      setName('');
      setAdvantages([{ type: 'RESTAURANT', yearlyLimit: 0 }]);
    } catch (err: any) {
      console.error('Erreur lors de la sauvegarde:', err.response?.data || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <Box p={3} width={420}>
        <Typography variant="h6" mb={2} color="#714291">
          {editingCategory ? 'Edit Category' : 'Add New Category'}
        </Typography>

  
        <TextField
          fullWidth
          label="Category Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          sx={{ mb: 3 }}
        />

   
        <Typography variant="subtitle1" mb={1}>
          Advantages
        </Typography>

        <Stack spacing={2}>
          {advantages.map((adv, index) => (
            <Stack key={index} direction="row" spacing={1} alignItems="center">
              <TextField
                select
                label="Type"
                value={adv.type}
                onChange={(e) => {
                  const newList = [...advantages];
                  newList[index].type = e.target.value;
                  setAdvantages(newList);
                }}
                sx={{ flex: 1 }}
              >
                {Object.values(ADVANTAGE_TYPE).map((val) => (
                  <MenuItem key={val} value={val}>
                    {val}
                  </MenuItem>
                ))}
              </TextField>

             <TextField
  type="number"
  label="Yearly Limit (€)"
  value={adv.yearlyLimit}
  onChange={(e) => {
    const newList = [...advantages];
    newList[index].yearlyLimit = e.target.value === '' ? '' : Number(e.target.value);
    setAdvantages(newList);
  }}
  sx={{ flex: 1 }}
/>


              <IconButton color="error" onClick={() => handleRemoveAdvantage(index)}>
                <Delete />
              </IconButton>
            </Stack>
          ))}
        </Stack>

        <Button
          variant="outlined"
          startIcon={<Add />}
          onClick={handleAddAdvantage}
          sx={{ mt: 2 }}
        >
          Add Advantage
        </Button>

      
        <Stack direction="row" spacing={2} mt={4}>
          <Button
            fullWidth
            variant="contained"
            onClick={handleSubmit}
            disabled={loading || !name.trim()}
            sx={{
              backgroundColor: '#714291',
              '&:hover': { backgroundColor: '#5a336f' },
            }}
          >
            {loading ? 'Saving...' : editingCategory ? 'Update' : 'Save'}
          </Button>

          <Button fullWidth variant="outlined" onClick={onClose}>
            Cancel
          </Button>
        </Stack>
      </Box>
    </Drawer>
  );
}
